import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PlaidLink, PlaidLinkOnSuccessMetadata } from "react-plaid-link";
import {
  getPaymentAccounts,
  getPaymentLinkToken,
  exchangePublicToken,
  removePaymentAccount,
} from "../../api/services";

interface PaymentAccountDto {
  id: number;
  bankAccountId: string;
  mask: string;
  institution: {
    id: number;
    bankName: string;
    logoUrl: string | null;
    url: string;
  };
}

interface Props {
  applicationId: string;
}

export const PaymentAccountsCard: React.FC<Props> = ({ applicationId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<PaymentAccountDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchAccounts = () => {
    setLoading(true);
    setError(null);
    getPaymentAccounts()
      .then((res) => {
        setAccounts(res.data);
      })
      .catch((err) => {
        console.error(err);
        setError(t("financial.accounts.errorFetching"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAccounts();
  }, [applicationId, t]);

  useEffect(() => {
    if (!loading && accounts.length === 0) {
      getPaymentLinkToken()
        .then((res) => {
          setLinkToken(res.data.link_token);
        })
        .catch((err) => {
          console.error(err);
          setError(t("financial.accounts.errorFetchingLinkToken"));
        });
    }
  }, [accounts, loading, t]);

  const handleDelete = (acctId: number) => {
    // Tenants must keep at least one bank account on file. The button is
    // disabled in this case, but guard here too so it can't be triggered.
    if (accounts.length <= 1) {
      setError(
        t(
          "financial.accounts.errorLastAccount",
          "You must keep at least one bank account on file. Add another before removing this one."
        )
      );
      return;
    }
    if (!window.confirm(t("financial.accounts.confirmDelete"))) return;
    removePaymentAccount(acctId)
      .then(() => {
        fetchAccounts();
      })
      .catch((err) => {
        console.error(err);
        setError(t("financial.accounts.errorDeleting"));
      });
  };

  const onPlaidSuccess = async (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata
  ) => {
    setUploading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await exchangePublicToken(publicToken, metadata);
      const data = response.data;

      if (data.connectedAccounts) {
        const count = data.connectedAccounts;
        const message =
          count === 1
            ? t(
                "financial.accounts.connectedSingle",
                "Successfully connected 1 bank account"
              )
            : t(
                "financial.accounts.connectedMultiple",
                "Successfully connected {{count}} bank accounts",
                { count }
              );
        setSuccessMessage(message);
      } else {
        setSuccessMessage(
          t(
            "financial.accounts.connectedDefault",
            "Bank account connected successfully"
          )
        );
      }

      fetchAccounts();

      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error(err);
      // Prefer the API's reason — the exchange fails for specific, fixable
      // causes and the generic copy hides them.
      const apiMessage = err?.response?.data?.message;
      setError(
        typeof apiMessage === "string" && apiMessage.trim()
          ? apiMessage
          : t("financial.accounts.errorCompleting"),
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mb-8">
      <h2 className="mb-4">
        {t("financial.accounts.title", "Connected Bank Accounts")}
      </h2>
      <div className="rounded-xl bg-white shadow-sm">
        <div className="p-5">
          {loading ? (
            <p>{t("financial.accounts.loading", "Loading…")}</p>
          ) : error ? (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">{error}</div>
          ) : successMessage ? (
            <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-success">{successMessage}</div>
          ) : null}

          {!loading && !error && accounts.length > 0 ? (
            <div>
              {successMessage && (
                <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-success mb-4">{successMessage}</div>
              )}

              <div className="mb-4">
                <span className="text-sm text-muted">
                  {t(
                    "financial.accounts.summary",
                    "{{count}} account(s) connected",
                    { count: accounts.length }
                  )}
                </span>
              </div>

              <table className="w-full text-sm mb-6">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted">{t("financial.accounts.colBank", "Bank")}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted">{t("financial.accounts.colMask", "Account")}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted"></th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((acct) => (
                    <tr key={acct.id}>
                      <td className="px-4 py-3">
                        {acct.institution.logoUrl && (
                          <img
                            src={acct.institution.logoUrl}
                            alt={acct.institution.bankName}
                            height={24}
                            className="mr-2 inline-block"
                          />
                        )}
                        {acct.institution.bankName}
                      </td>
                      <td className="px-4 py-3">••••{acct.mask}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          className="rounded-md border border-danger px-3 py-1.5 text-xs font-medium text-danger transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-muted disabled:text-muted disabled:hover:bg-transparent"
                          onClick={() => handleDelete(acct.id)}
                          disabled={accounts.length <= 1}
                          title={
                            accounts.length <= 1
                              ? t(
                                  "financial.accounts.lastAccountHint",
                                  "You must keep at least one bank account on file."
                                )
                              : undefined
                          }
                        >
                          {t("financial.accounts.delete", "Remove")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center">
              <p>
                <img alt="Plaid logo" src="/assets/plaid.svg" />
              </p>
              <h5>{t("financial.plaidTitle", "Bank Verification")}</h5>
              <p>
                {t("financial.intro1", "We use Plaid to confirm your banking")}
              </p>
              <p>
                <strong>
                  {t("financial.intro3", "This only takes a moment")}
                  <br />
                  {t("financial.intro4", "All data is secure")}
                </strong>
              </p>
              <p>
                {t(
                  "financial.intro5",
                  "You may be prompted to log in to your bank."
                )}
              </p>
              <p className="text-sm text-muted">
                {t(
                  "financial.intro6",
                  "All eligible checking and savings accounts will be connected automatically."
                )}
              </p>
              {linkToken ? (
                <PlaidLink
                  token={linkToken}
                  onSuccess={onPlaidSuccess}
                  onExit={(err, metadata) =>
                    console.error("Plaid exit:", err, metadata)
                  }
                  className="rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-black transition-colors hover:bg-accent-hover cursor-pointer border-0"
                >
                  {uploading
                    ? t("financial.accounts.connecting", "Connecting…")
                    : t("financial.accounts.connect", "Connect Bank Accounts")}
                </PlaidLink>
              ) : (
                <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-warning">
                  {t(
                    "financial.accounts.noLinkToken",
                    "Unable to initialize Plaid."
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
